import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  CreditCard, ShieldCheck, ShieldX, Search, AlertTriangle,
  Calendar, User, X
} from 'lucide-react';
import StepUpAuthModal, { getValidStepUpToken, PreviewSecurityBanner } from '../components/StepUpAuthModal';

const STATUS_CONFIG = {
  active:  { label: 'Active',  color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  revoked: { label: 'Revoked', color: 'bg-red-100 text-red-700', icon: ShieldX },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-500', icon: AlertTriangle },
};

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
}

// ─── Revoke Modal ─────────────────────────────────────────────────────

function RevokeModal({ auth, onClose, onRevoke }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRevoke = async () => {
    if (reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    await onRevoke(auth.authorization_id, reason.trim());
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" data-testid="revoke-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Revoke Payment Authorization</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          This will permanently prevent any future off-session charges using this authorization. This action cannot be undone.
        </div>
        <div className="text-sm space-y-1 text-slate-600">
          <p><strong>Booking:</strong> {auth.booking_id}</p>
          <p><strong>Customer:</strong> {auth.customer?.full_name || auth.customer_id}</p>
          <p><strong>Card:</strong> {auth.payment_method_brand} ****{auth.payment_method_last4}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Revocation Reason (min 10 chars)</Label>
          <Input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Customer requested cancellation of stored payment method"
            className="text-sm"
            data-testid="revoke-reason-input"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={submitting || reason.trim().length < 10}
            onClick={handleRevoke}
            data-testid="confirm-revoke-btn"
          >
            {submitting ? 'Revoking...' : 'Revoke Authorization'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────

export default function PaymentAuthorizationsPage() {
  const { api } = useAuth();
  const [authorizations, setAuthorizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [showStepUp, setShowStepUp] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');
      const res = await api.get(`/platform-owner/payment-authorizations?${params.toString()}`);
      setAuthorizations(res.data.authorizations || []);
      setTotal(res.data.total || 0);
    } catch { setAuthorizations([]); setTotal(0); }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRevoke = async (authId, reason) => {
    const executeRevoke = async () => {
      try {
        await api.post(`/platform-owner/payment-authorizations/${authId}/revoke`, { reason });
        toast.success('Authorization revoked');
        setRevokeTarget(null);
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to revoke');
      }
    };

    const existing = getValidStepUpToken();
    if (existing) {
      await executeRevoke();
    } else {
      setPendingRevoke(() => executeRevoke);
      setShowStepUp(true);
    }
  };

  const filtered = searchQuery
    ? authorizations.filter(a => {
        const q = searchQuery.toLowerCase();
        return (
          (a.authorization_id || '').toLowerCase().includes(q) ||
          (a.booking_id || '').toLowerCase().includes(q) ||
          (a.customer?.full_name || '').toLowerCase().includes(q) ||
          (a.customer?.email || '').toLowerCase().includes(q) ||
          (a.payment_method_last4 || '').includes(q)
        );
      })
    : authorizations;

  const activeCt = authorizations.filter(a => a.status === 'active').length;
  const revokedCt = authorizations.filter(a => a.status === 'revoked').length;

  return (
    <PlatformOwnerAppShell activeId="payment-auths" title="Payment Authorizations">
      <div className="space-y-4" data-testid="payment-auths-page">
        <PreviewSecurityBanner />
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Payment Authorizations</h2>
            <p className="text-sm text-slate-500">
              {total} stored authorization{total !== 1 ? 's' : ''} &middot; {activeCt} active &middot; {revokedCt} revoked
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by booking ID, customer, card..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 text-sm"
              data-testid="auth-search-input"
            />
          </div>
          <div className="flex gap-2">
            {['', 'active', 'revoked', 'expired'].map(s => (
              <Button
                key={s || 'all'}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                className="text-xs"
                onClick={() => setStatusFilter(s)}
                data-testid={`auth-filter-${s || 'all'}`}
              >
                {s ? (STATUS_CONFIG[s]?.label || s) : 'All'}
              </Button>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-400" data-testid="empty-state">
                <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No payment authorizations found</p>
                <p className="text-sm mt-1">
                  {total === 0
                    ? 'Stored payment authorizations will appear here when renters save payment methods for bookings.'
                    : 'No authorizations match your current filter.'}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm" data-testid="auths-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Authorization</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Booking</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Customer</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Card</th>
                        <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Created</th>
                        <th className="text-center px-4 py-3 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.map(a => {
                        const cfg = STATUS_CONFIG[a.status] || { label: a.status, color: 'bg-gray-100 text-gray-600' };
                        return (
                          <tr key={a.authorization_id} className="hover:bg-slate-50" data-testid={`auth-row-${a.authorization_id}`}>
                            <td className="px-4 py-3 font-mono text-xs text-slate-600">{(a.authorization_id || '').slice(0, 16)}...</td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-600">{(a.booking_id || '').slice(0, 16)}...</td>
                            <td className="px-4 py-3">
                              <span className="text-slate-900">{a.customer?.full_name || '-'}</span>
                              {a.customer?.email && <p className="text-xs text-slate-400">{a.customer.email}</p>}
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <span className="capitalize">{a.payment_method_brand || '-'}</span>
                              {a.payment_method_last4 && <span className="ml-1">****{a.payment_method_last4}</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500">{formatDate(a.created_at)}</td>
                            <td className="px-4 py-3 text-center">
                              {a.status === 'active' ? (
                                <Button variant="outline" size="sm" className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setRevokeTarget(a)} data-testid={`revoke-btn-${a.authorization_id}`}>
                                  Revoke
                                </Button>
                              ) : a.status === 'revoked' ? (
                                <span className="text-xs text-slate-400" title={a.revoked_reason}>{formatDate(a.revoked_at)}</span>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y">
                  {filtered.map(a => {
                    const cfg = STATUS_CONFIG[a.status] || { label: a.status, color: 'bg-gray-100 text-gray-600' };
                    return (
                      <div key={a.authorization_id} className="p-4 space-y-2" data-testid={`auth-card-${a.authorization_id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-mono text-xs text-slate-500">{(a.authorization_id || '').slice(0, 20)}...</p>
                            <p className="text-sm font-medium mt-0.5">{a.customer?.full_name || 'Unknown'}</p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span className="capitalize">{a.payment_method_brand} ****{a.payment_method_last4}</span>
                          <span>{formatDate(a.created_at)}</span>
                        </div>
                        <p className="text-xs text-slate-400 font-mono">{a.booking_id}</p>
                        {a.status === 'active' && (
                          <Button variant="outline" size="sm" className="w-full text-xs text-red-600" onClick={() => setRevokeTarget(a)}>
                            Revoke Authorization
                          </Button>
                        )}
                        {a.status === 'revoked' && a.revoked_reason && (
                          <p className="text-xs text-red-500 bg-red-50 p-2 rounded">Revoked: {a.revoked_reason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revoke Modal */}
      {revokeTarget && (
        <RevokeModal
          auth={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoke={handleRevoke}
        />
      )}
      <StepUpAuthModal
        isOpen={showStepUp}
        onSuccess={() => { setShowStepUp(false); if (pendingRevoke) pendingRevoke(); setPendingRevoke(null); }}
        onCancel={() => { setShowStepUp(false); setPendingRevoke(null); }}
        actionLabel="change financial settings"
      />
    </PlatformOwnerAppShell>
  );
}
